import { useContext, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { YStack } from '@/components/ui/layout/layout';

import { BrowserRequestPresentationContext } from './browser-request-presentation';

export const CredentialRequestOverlay = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const presentation = useContext(BrowserRequestPresentationContext);
  const inlineTarget = presentation?.inlineTarget;
  useEffect(() => {
    inlineTarget?.scrollIntoView({ block: 'nearest' });
  }, [inlineTarget]);

  // A chat request waits for its host instead of covering the live browser.
  if (presentation && !inlineTarget) return null;

  // The form owns its draft above this portal, so changing surfaces keeps inputs
  if (inlineTarget) {
    return createPortal(
      <YStack role="region" aria-label={label} className="w-full">
        {children}
      </YStack>,
      inlineTarget
    );
  }

  return (
    <YStack
      center
      className="pointer-events-none absolute inset-0 z-10 overflow-y-auto bg-black/50 p-4"
    >
      <YStack
        role="dialog"
        aria-label={label}
        className="pointer-events-auto max-h-full w-full max-w-sm overflow-y-auto rounded-lg shadow-lg"
      >
        {children}
      </YStack>
    </YStack>
  );
};
